// Key để lưu trong localStorage, giống với cookie keys
const FB_STORAGE_KEY = 'fb_cookie_data'

// ==================== Facebook Cookie Utils ====================

/**
 * Lấy Facebook cookie data từ localStorage
 */
export const getFacebookCookieFromStorage = (): IFacebookCookie | null => {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(FB_STORAGE_KEY)
    if (!stored) return null

    return JSON.parse(stored) as IFacebookCookie
  } catch (error) {
    console.error('Error getting Facebook cookie from storage:', error)
    return null
  }
}

/**
 * Lưu Facebook cookie data vào localStorage
 */
export const saveFacebookCookieToStorage = (data: IFacebookCookie): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(FB_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.error('Error saving Facebook cookie to storage:', error)
  }
}

/**
 * So sánh 2 Facebook cookie objects để kiểm tra có thay đổi không
 */
export const isFacebookCookieChanged = (
  oldData: IFacebookCookie | null,
  newData: IFacebookCookie
): boolean => {
  if (!oldData) return true

  return oldData._fbp !== newData._fbp || oldData._fbc !== newData._fbc
}

/**
 * Xóa Facebook cookie data khỏi localStorage
 */
export const clearFacebookCookieFromStorage = (): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(FB_STORAGE_KEY)
  } catch (error) {
    console.error('Error clearing Facebook cookie from storage:', error)
  }
}

// ==================== Referral Tracking Utils ====================

// Key để lưu referral data đầy đủ trong localStorage
const REFERRAL_DATA_STORAGE_KEY = 'referral_tracking_data'

/**
 * Parse URL parameters từ targetUrl
 */
export const parseURLParams = (url: string): IURLParams => {
  try {
    const urlObj = new URL(url)
    return {
      camp: urlObj.searchParams.get('camp'),
      adsid: urlObj.searchParams.get('adsid') || urlObj.searchParams.get('adsId'),
      platform: urlObj.searchParams.get('platform'),
      type: urlObj.searchParams.get('type'),
      fbclid: urlObj.searchParams.get('fbclid'),
      gclid: urlObj.searchParams.get('gclid'),
      blogUrl: urlObj.searchParams.get('blog_url'),
      srcBlog: urlObj.searchParams.get('source'),
    }
  } catch (error) {
    console.error('Error parsing URL:', error)
    return {
      camp: null,
      adsid: null,
      platform: null,
      type: null,
      fbclid: null,
      gclid: null,
      blogUrl: null,
      srcBlog: null,
    }
  }
}

/**
 * Lưu referral data đầy đủ vào localStorage
 * Được gọi ngay khi detect referral params (kể cả chưa login)
 */
export const saveReferralDataToStorage = (
  referralSource: string | null,
  targetUrl: string | null,
  mailId: string,
  determineReferralTypeFn: (params: IDetermineReferralTypeParams) => ReferralType
): void => {
  if (typeof window === 'undefined') return

  try {
    // Parse URL params
    let params: IURLParams = {
      camp: null,
      adsid: null,
      platform: null,
      type: null,
      fbclid: null,
      gclid: null,
      blogUrl: null,
      srcBlog: null,
    }

    if (targetUrl) {
      params = parseURLParams(targetUrl)
    }

    // Determine referral type
    const referralType = determineReferralTypeFn({
      fbclid: params.fbclid,
      gclid: params.gclid,
      referralSource,
      adsid: params.adsid,
      platform: params.platform,
      mailId,
      blogUrl: params.blogUrl,
      srcBlog: params.srcBlog,
    })

    // Build stored data
    const storedData: IStoredReferralData = {
      referral_source: referralSource,
      referral_type: referralType,
      target_url: targetUrl,
      camp: params.camp,
      adsid: params.adsid,
      platform: params.platform,
      type: params.type,
      mailing_id: Number(mailId) || 0,
      captured_at: Date.now(),
      is_sent: false,
    }

    localStorage.setItem(REFERRAL_DATA_STORAGE_KEY, JSON.stringify(storedData))
  } catch (error) {
    console.error('[Referral] Error saving referral data to storage:', error)
  }
}

/**
 * Lấy referral data từ localStorage
 */
export const getStoredReferralData = (): IStoredReferralData | null => {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(REFERRAL_DATA_STORAGE_KEY)
    if (!stored) return null

    return JSON.parse(stored) as IStoredReferralData
  } catch (error) {
    console.error('[Referral] Error getting stored referral data:', error)
    return null
  }
}

/**
 * Mark referral data là đã gửi
 */
export const markReferralDataAsSent = (): void => {
  if (typeof window === 'undefined') return

  try {
    const stored = getStoredReferralData()
    if (!stored) return

    stored.is_sent = true
    localStorage.setItem(REFERRAL_DATA_STORAGE_KEY, JSON.stringify(stored))
    console.log('[Referral] Marked referral data as sent')
  } catch (error) {
    console.error('[Referral] Error marking referral data as sent:', error)
  }
}

/**
 * Clear referral data từ localStorage
 * Chỉ gọi sau khi save action log thành công
 */
export const clearStoredReferralData = (): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(REFERRAL_DATA_STORAGE_KEY)
    // Also clear old keys for backward compatibility
    localStorage.removeItem('referralSource')
    localStorage.removeItem('targetUrl')
    localStorage.removeItem('mailing_id')
    console.log('[Referral] Cleared referral data from storage')
  } catch (error) {
    console.error('[Referral] Error clearing referral data:', error)
  }
}

/**
 * Build referral data object để gửi lên server
 * @deprecated Sử dụng getStoredReferralData() và buildReferralDataFromStored() thay thế
 */
export const buildReferralData = (
  referralSource: string | null,
  targetUrl: string | null,
  mailId: string,
  determineReferralTypeFn: (params: IDetermineReferralTypeParams) => ReferralType
): string => {
  // Default values
  let params: IURLParams = {
    camp: null,
    adsid: null,
    platform: null,
    type: null,
    fbclid: null,
    gclid: null,
    blogUrl: null,
    srcBlog: null,
  }

  // Parse URL nếu có targetUrl
  if (targetUrl) {
    params = parseURLParams(targetUrl)
  }

  // Xác định referral type
  const referralType = determineReferralTypeFn({
    fbclid: params.fbclid,
    gclid: params.gclid,
    referralSource,
    adsid: params.adsid,
    platform: params.platform,
    mailId,
    blogUrl: params.blogUrl,
    srcBlog: params.srcBlog,
  })

  // Build referral data object
  const referralData: IReferralData = {
    referral_source: referralSource,
    referral_type: referralType,
    target_url: targetUrl,
    camp: params.camp,
    adsid: params.adsid,
    platform: params.platform,
    type: params.type,
    mailing_id: Number(mailId) || 0,
  }

  return JSON.stringify(referralData)
}

/**
 * Build referral data JSON string từ stored data
 */
export const buildReferralDataFromStored = (storedData: IStoredReferralData): string => {
  const referralData: IReferralData = {
    referral_source: storedData.referral_source,
    referral_type: storedData.referral_type,
    target_url: storedData.target_url,
    camp: storedData.camp,
    adsid: storedData.adsid,
    platform: storedData.platform,
    type: storedData.type,
    mailing_id: storedData.mailing_id,
  }

  return JSON.stringify(referralData)
}

/**
 * Get referral data object để gửi khi đăng ký/login
 * Trả về object thay vì JSON string
 */
export const getReferralDataForAuth = (): IReferralData | null => {
  const storedData = getStoredReferralData()
  if (!storedData) return null

  return {
    referral_source: storedData.referral_source,
    referral_type: storedData.referral_type,
    target_url: storedData.target_url,
    camp: storedData.camp,
    adsid: storedData.adsid,
    platform: storedData.platform,
    type: storedData.type,
    mailing_id: storedData.mailing_id,
  }
}

/**
 * Kiểm tra xem có nên log action hay không (dựa trên lastAction time)
 * @param lastActionTimestamp - Timestamp của lastAction
 * @param thresholdMs - Thời gian tối thiểu giữa 2 lần log (default: 1 giờ)
 * @returns true nếu nên log, false nếu skip
 */
export const shouldLogAction = (
  lastActionTimestamp: string | null,
  thresholdMs: number = 3600000
): boolean => {
  if (!lastActionTimestamp) return true

  const now = new Date()
  const timeSinceLastAction = now.getTime() - Number(lastActionTimestamp)

  return timeSinceLastAction >= thresholdMs
}

/**
 * Cleanup referral data từ localStorage sau khi log thành công
 * @deprecated Sử dụng clearStoredReferralData() thay thế
 */
export const cleanupReferralData = (): void => {
  clearStoredReferralData()
}

/**
 * Get referral data từ localStorage (old format - backward compatibility)
 * @deprecated Sử dụng getStoredReferralData() thay thế
 */
export const getReferralDataFromStorage = () => {
  if (typeof window === 'undefined') {
    return {
      referralSource: null,
      targetUrl: null,
      mailId: '',
    }
  }

  return {
    referralSource: localStorage.getItem('referralSource'),
    targetUrl: localStorage.getItem('targetUrl'),
    mailId: localStorage.getItem('mailing_id') || '',
  }
}

/**
 * Kiểm tra xem có referral data cần xử lý không
 * - Có data stored
 - Chưa được gửi
 * - Chưa quá cũ (mặc định 7 ngày)
 */
export const hasValidReferralData = (maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): boolean => {
  const storedData = getStoredReferralData()
  if (!storedData) return false
  if (storedData.is_sent) return false

  const now = Date.now()
  const age = now - storedData.captured_at

  return age < maxAgeMs
}
